import {
  type ChangeEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type DayPickerProps, type Matcher } from 'react-day-picker';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { add, format, isValid } from 'date-fns';
import { withMask } from 'use-mask-input';
import { enUS } from 'date-fns/locale';
import {
  DateTimeGranularity,
  Period,
  TimePickerType,
  display12HourValue,
  getArrowByType,
  getDateByType,
  getDateTimeInputConfig,
  parseDateTimeInput,
  setDateByType,
  validateDateTimeInputString,
} from '@/utilities/date';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KeyboardKey } from '@/lib/keyboard';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

interface PeriodSelectorProps {
  period: Period;
  setPeriod?: (m: Period) => void;
  date?: Date | null;
  onDateChange?: (date: Date | undefined) => void;
  onRightFocus?: () => void;
  onLeftFocus?: () => void;
}

const TimePeriodSelect = forwardRef<HTMLButtonElement, PeriodSelectorProps>(
  ({ period, setPeriod, date, onDateChange, onLeftFocus, onRightFocus }, ref) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === KeyboardKey.ArrowRight) {
        onRightFocus?.();
      }
      if (e.key === KeyboardKey.ArrowLeft) {
        onLeftFocus?.();
      }
    };

    const handleValueChange = (value: Period) => {
      setPeriod?.(value);

      /**
       * trigger an update whenever the user switches between AM and PM;
       * otherwise user must manually change the hour each time
       */
      if (date) {
        const tempDate = new Date(date);
        const hours = display12HourValue(date.getHours());
        onDateChange?.(
          setDateByType(
            tempDate,
            hours.toString(),
            TimePickerType.TwelveHours,
            period === Period.AM ? Period.PM : Period.AM,
          ),
        );
      }
    };

    return (
      <div className="flex h-10 items-center">
        <Select defaultValue={period} onValueChange={(value: Period) => handleValueChange(value)}>
          <SelectTrigger ref={ref} className="w-16.25" onKeyDown={handleKeyDown}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={Period.AM}>AM</SelectItem>
            <SelectItem value={Period.PM}>PM</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  },
);

TimePeriodSelect.displayName = 'TimePeriodSelect';

interface TimePickerInputProps extends InputHTMLAttributes<HTMLInputElement> {
  picker: TimePickerType;
  date?: Date | null;
  onDateChange?: (date: Date | undefined) => void;
  period?: Period;
  onRightFocus?: () => void;
  onLeftFocus?: () => void;
}

const TimePickerInput = forwardRef<HTMLInputElement, TimePickerInputProps>(
  (
    {
      className,
      type = 'tel',
      value,
      id,
      name,
      date = new Date(new Date().setHours(0, 0, 0, 0)),
      onDateChange,
      onChange,
      onKeyDown,
      picker,
      period,
      onLeftFocus,
      onRightFocus,
      ...props
    },
    ref,
  ) => {
    const [flag, setFlag] = useState<boolean>(false);
    const [prevIntKey, setPrevIntKey] = useState<string>('0');

    /**
     * allow the user to enter the second digit within 2 seconds
     * otherwise start again with entering first digit
     */
    useEffect(() => {
      if (flag) {
        const timer = setTimeout(() => {
          setFlag(false);
        }, 2000);

        return () => clearTimeout(timer);
      }
    }, [flag]);

    const calculatedValue = useMemo(() => {
      return getDateByType(date, picker);
    }, [date, picker]);

    const calculateNewValue = (key: string) => {
      /*
       * If picker is '12hours' and the first digit is 0, then the second digit is automatically set to 1.
       * The second entered digit will break the condition and the value will be set to 10-12.
       */
      if (picker === TimePickerType.TwelveHours) {
        if (flag && calculatedValue.slice(1, 2) === '1' && prevIntKey === '0') {
          return `0${key}`;
        }
      }

      return !flag ? `0${key}` : calculatedValue.slice(1, 2) + key;
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === KeyboardKey.Tab) {
        return;
      }
      if (e.key === KeyboardKey.Enter) {
        e.preventDefault();
        e.currentTarget.blur();
        return;
      }
      e.preventDefault();
      if (e.key === KeyboardKey.ArrowRight) {
        onRightFocus?.();
      }
      if (e.key === KeyboardKey.ArrowLeft) {
        onLeftFocus?.();
      }
      if (e.key === KeyboardKey.ArrowUp || e.key === KeyboardKey.ArrowDown) {
        const step = e.key === KeyboardKey.ArrowUp ? 1 : -1;
        const newValue = getArrowByType(calculatedValue, step, picker);
        if (flag) {
          setFlag(false);
        }
        const tempDate = date ? new Date(date) : new Date();
        onDateChange?.(setDateByType(tempDate, newValue, picker, period));
      }
      if (e.key >= '0' && e.key <= '9') {
        if (picker === TimePickerType.TwelveHours) {
          setPrevIntKey(e.key);
        }

        const newValue = calculateNewValue(e.key);
        if (flag) {
          onRightFocus?.();
        }
        setFlag((prev) => !prev);
        const tempDate = date ? new Date(date) : new Date();
        onDateChange?.(setDateByType(tempDate, newValue, picker, period));
      }
    };

    return (
      <Input
        ref={ref}
        id={id ?? picker}
        name={name ?? picker}
        className={cn('h-6 w-[48px] bg-input-background dark:focus-within:ring-offset-neutral-950', className)}
        value={value ?? calculatedValue}
        onChange={(e) => {
          e.preventDefault();
          onChange?.(e);
        }}
        type={type}
        inputMode="decimal"
        blurOnEnter={false}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          handleKeyDown(e);
        }}
        {...props}
      />
    );
  },
);

TimePickerInput.displayName = 'TimePickerInput';

interface TimePickerProps {
  date?: Date | null;
  onChange?: (date: Date | undefined) => void;
  hourCycle?: 12 | 24;
  /**
   * Determines the smallest unit that is displayed in the datetime picker.
   * Default is 'second'.
   * */
  granularity?: Granularity;
}

interface TimePickerRef {
  minuteRef: HTMLInputElement | null;
  hourRef: HTMLInputElement | null;
  secondRef: HTMLInputElement | null;
}

const TimePicker = forwardRef<TimePickerRef, TimePickerProps>(
  ({ date: initDate, onChange, hourCycle = 24, granularity = DateTimeGranularity.Second }, ref) => {
    const date = initDate && new Date(initDate);
    const minuteRef = useRef<HTMLInputElement>(null);
    const hourRef = useRef<HTMLInputElement>(null);
    const secondRef = useRef<HTMLInputElement>(null);
    const periodRef = useRef<HTMLButtonElement>(null);
    const [period, setPeriod] = useState<Period>(date && date.getHours() >= 12 ? Period.PM : Period.AM);

    useImperativeHandle(
      ref,
      () => ({
        minuteRef: minuteRef.current,
        hourRef: hourRef.current,
        secondRef: secondRef.current,
        periodRef: periodRef.current,
      }),
      [minuteRef, hourRef, secondRef],
    );

    return (
      <div className="flex items-center justify-center gap-2">
        <label htmlFor="datetime-picker-hour-input" className="cursor-pointer">
          <Clock className="mr-2 icon-4" />
        </label>
        <TimePickerInput
          picker={hourCycle === 24 ? TimePickerType.Hours : TimePickerType.TwelveHours}
          date={date}
          id="datetime-picker-hour-input"
          onDateChange={onChange}
          ref={hourRef}
          period={period}
          onRightFocus={() => minuteRef?.current?.focus()}
        />
        {(granularity === DateTimeGranularity.Minute || granularity === DateTimeGranularity.Second) && (
          <>
            :
            <TimePickerInput
              picker={TimePickerType.Minutes}
              date={date}
              onDateChange={onChange}
              ref={minuteRef}
              onLeftFocus={() => hourRef?.current?.focus()}
              onRightFocus={() => secondRef?.current?.focus()}
            />
          </>
        )}
        {granularity === DateTimeGranularity.Second && (
          <>
            :
            <TimePickerInput
              picker={TimePickerType.Seconds}
              date={date}
              onDateChange={onChange}
              ref={secondRef}
              onLeftFocus={() => minuteRef?.current?.focus()}
              onRightFocus={() => periodRef?.current?.focus()}
            />
          </>
        )}
        {hourCycle === 12 && (
          <div className="grid gap-1 text-center">
            <TimePeriodSelect
              period={period}
              setPeriod={setPeriod}
              date={date}
              onDateChange={(newDate) => {
                onChange?.(newDate);
                if (newDate && newDate?.getHours() >= 12) {
                  setPeriod(Period.PM);
                } else {
                  setPeriod(Period.AM);
                }
              }}
              ref={periodRef}
              onLeftFocus={() => secondRef?.current?.focus()}
            />
          </div>
        )}
      </div>
    );
  },
);
TimePicker.displayName = 'TimePicker';

type Granularity = DateTimeGranularity;

type DateTimePickerProps = {
  value?: Date | string | null;
  onChange?: (date: Date | string | undefined) => void;
  onMonthChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  open?: boolean;
  setOpen?: (value: boolean) => void;
  indeterminate?: boolean;
  /** showing `AM/PM` or not. */
  hourCycle?: 12 | 24;
  disabledDates?: Matcher;
  placeholder?: string;
  startMonth?: Date;
  endMonth?: Date;
  /**
   * The format is derived from the `date-fns` documentation.
   * @reference https://date-fns.org/v3.6.0/docs/format
   **/
  displayFormat?: { hour24?: string; hour12?: string };
  /**
   * The granularity prop allows you to control the smallest unit that is displayed by DateTimePicker.
   * By default, the value is `second` which shows all time inputs.
   **/
  granularity?: Granularity;
  className?: string;
  /**
   * Show the default month and time when popup the calendar. Default is the current Date().
   **/
  defaultPopupValue?: Date;
  /**
   * If `true`, the calendar will not be rendered outside of the component's container.
   * @default false
   * */
  noPortal?: boolean;
} & Pick<DayPickerProps, 'locale' | 'weekStartsOn' | 'showWeekNumber' | 'showOutsideDays'>;

type DateTimePickerRef = {
  value?: Date;
  /** forward .focus() so consumers can open the native input’s keyboard/focus state */
  focus: () => void;
};

const DateTimePicker = forwardRef<Partial<DateTimePickerRef>, DateTimePickerProps>(
  (
    {
      locale = enUS,
      defaultPopupValue = new Date(new Date().setHours(0, 0, 0, 0)),
      value,
      onChange,
      onMonthChange,
      open,
      setOpen,
      indeterminate,
      hourCycle = 24,
      disabled = false,
      disabledDates,
      displayFormat: _displayFormat,
      granularity = DateTimeGranularity.Second,
      placeholder,
      className,
      startMonth,
      endMonth,
      noPortal = false,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const inputConfig = useMemo(() => getDateTimeInputConfig(granularity), [granularity]);
    const inputFormat = inputConfig.format;
    const inputLength = inputConfig.length;
    const tCommon = useTranslations('common');
    const resolvedPlaceholder = placeholder ?? inputConfig.placeholder;
    const maskPattern = inputConfig.mask;
    const inputMaskRef = useRef<HTMLInputElement | null>(null);
    const iconButtonRef = useRef<HTMLButtonElement | null>(null);
    const maskRef = useMemo(
      () =>
        withMask(maskPattern, {
          showMaskOnHover: false,
          showMaskOnFocus: true,
          clearMaskOnLostFocus: true,
        }),
      [maskPattern],
    );

    const resolveDateValue = (input: Date | string | null | undefined) => {
      if (!input) {
        return undefined;
      }
      const nextDate = input instanceof Date ? input : new Date(input);
      return isValid(nextDate) ? nextDate : undefined;
    };

    const initialDateValue = resolveDateValue(value);

    const [month, setMonth] = useState<Date>(initialDateValue ?? defaultPopupValue);
    const [displayDate, setDisplayDate] = useState<Date | undefined>(initialDateValue);
    const [inputValue, setInputValue] = useState(() => {
      if (initialDateValue) {
        return format(initialDateValue, inputFormat);
      }
      if (typeof value === 'string') {
        return value;
      }
      return '';
    });
    const [internalOpen, setInternalOpen] = useState(false);

    const popoverOpen = open ?? internalOpen;
    const handleOpenChange = (nextOpen: boolean) => {
      if (nextOpen === popoverOpen) {
        return;
      }
      if (open === undefined) {
        setInternalOpen(nextOpen);
      }
      setOpen?.(nextOpen);
    };
    // onMonthChange ||= onChange;

    useEffect(() => {
      if (!inputMaskRef.current) {
        return;
      }

      maskRef(inputMaskRef.current);

      return () => {
        maskRef(null);
      };
    }, [maskRef]);

    useEffect(() => {
      if (!popoverOpen || !inputMaskRef.current) {
        return;
      }

      requestAnimationFrame(() => {
        inputMaskRef.current?.focus();
      });
    }, [popoverOpen]);

    /**
     * Makes sure display date updates when value change on
     * parent component
     */
    useEffect(() => {
      const nextDate = resolveDateValue(value);
      setDisplayDate(nextDate);
      if (nextDate) {
        setMonth(nextDate);
        setInputValue(format(nextDate, inputFormat));
        return;
      }
      if (typeof value === 'string') {
        setInputValue(value);
        return;
      }
      setInputValue('');
    }, [value, inputFormat]);

    /**
     * carry over the current time when a user clicks a new day
     * instead of resetting to 00:00
     */
    const handleMonthChange = (newDay: Date | undefined) => {
      if (!newDay) {
        return;
      }
      if (!defaultPopupValue) {
        newDay.setHours(month?.getHours() ?? 0, month?.getMinutes() ?? 0, month?.getSeconds() ?? 0);
        onMonthChange?.(newDay);
        setMonth(newDay);
        return;
      }
      const diff = newDay.getTime() - defaultPopupValue.getTime();
      const diffInDays = diff / (1000 * 60 * 60 * 24);
      const newDateFull = add(defaultPopupValue, { days: Math.ceil(diffInDays) });
      newDateFull.setHours(month?.getHours() ?? 0, month?.getMinutes() ?? 0, month?.getSeconds() ?? 0);
      onMonthChange?.(newDateFull);
      setMonth(newDateFull);
    };

    const onSelect = (newDay?: Date) => {
      if (!newDay) {
        return;
      }
      onChange?.(newDay);
      setMonth(newDay);
      setDisplayDate(newDay);
      setInputValue(format(newDay, inputFormat));
    };

    useImperativeHandle(
      ref,
      () => ({
        ...inputRef.current,
        value: displayDate,
        focus: () => {
          inputRef.current?.focus();
        },
      }),
      [displayDate],
    );

    return (
      <Input
        disabled={disabled}
        indeterminate={indeterminate}
        type="text"
        ref={inputRef}
        className={cn('px-0', className)}
        {...props}
        safeInputRef={inputMaskRef}
        inputMode="numeric"
        maxLength={inputLength}
        onClick={(e) => {
          (e.target as HTMLInputElement).focus();
          if (!disabled && !popoverOpen) {
            handleOpenChange(true);
          }
        }}
        value={inputValue}
        placeholder={resolvedPlaceholder}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const nextValue = event.target.value;
          setInputValue(nextValue);

          if (nextValue.length === 0) {
            setDisplayDate(undefined);
            onChange?.(undefined);
            return;
          }

          if (nextValue.length < inputLength) {
            onChange?.(nextValue);
            return;
          }

          const parsedDate = parseDateTimeInput(nextValue, granularity);
          if (!parsedDate) {
            onChange?.(nextValue);
            return;
          }

          const validationResult = validateDateTimeInputString(nextValue, {
            granularity,
            hourCycle,
          });
          if (!validationResult.valid) {
            onChange?.(nextValue);
            return;
          }

          const normalizedDate = new Date(parsedDate);
          if (granularity === DateTimeGranularity.Day) {
            normalizedDate.setHours(0, 0, 0, 0);
          } else if (granularity === DateTimeGranularity.Hour) {
            normalizedDate.setMinutes(0, 0, 0);
          } else if (granularity === DateTimeGranularity.Minute) {
            normalizedDate.setSeconds(0, 0);
          }

          onChange?.(normalizedDate);
          setDisplayDate(normalizedDate);
          setMonth(normalizedDate);
          setInputValue(format(normalizedDate, inputFormat));
        }}
        prependIcon={
          <Popover modal={false} open={popoverOpen} onOpenChange={handleOpenChange}>
            <PopoverAnchor asChild>
              <Button
                ref={iconButtonRef}
                type="button"
                variant="ghost"
                disabled={disabled}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  handleOpenChange(true);
                  requestAnimationFrame(() => {
                    inputMaskRef.current?.focus();
                  });
                }}
                className={cn(
                  'mx-0.5 min-h-full rounded-[7px] border-0 p-0.75 duration-150 hover:bg-transparent! focus-visible:bg-ui-base focus-visible:ring-0 disabled:cursor-not-allowed',
                  indeterminate ? 'text-status-warning!' : 'text-icon-base! hover:text-text-base!',
                )}
                aria-label={tCommon('openDateSelector')}
              >
                <CalendarIcon className="icon-4" strokeWidth={1.5} />
              </Button>
            </PopoverAnchor>
            <PopoverContent
              noPortal={noPortal}
              className="w-auto p-0"
              onCloseAutoFocus={(event) => {
                event.preventDefault();
              }}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
              }}
              onFocusOutside={(event) => {
                const target = event.target as Node;
                if (target === inputMaskRef.current || iconButtonRef.current?.contains(target)) {
                  event.preventDefault();
                }
              }}
              onInteractOutside={(event) => {
                const target = event.target as Node;
                if (target === inputMaskRef.current || iconButtonRef.current?.contains(target)) {
                  event.preventDefault();
                }
              }}
            >
              <Calendar
                mode="single"
                selected={displayDate}
                disabled={disabledDates}
                startMonth={startMonth}
                endMonth={endMonth}
                month={month}
                onSelect={(newDate) => {
                  if (newDate) {
                    newDate.setHours(month?.getHours() ?? 0, month?.getMinutes() ?? 0, month?.getSeconds() ?? 0);
                    onSelect(newDate);
                  }
                }}
                onMonthChange={handleMonthChange}
                locale={locale}
                {...props}
              />
              {granularity !== DateTimeGranularity.Day && (
                <div className="border-t border-ui-bevel p-3">
                  <TimePicker
                    onChange={(timeValue) => {
                      onChange?.(timeValue);
                      setDisplayDate(timeValue);
                      if (timeValue) {
                        setMonth(timeValue);
                        setInputValue(format(timeValue, inputFormat));
                      } else {
                        setInputValue('');
                      }
                    }}
                    date={month}
                    hourCycle={hourCycle}
                    granularity={granularity}
                  />
                </div>
              )}
            </PopoverContent>
          </Popover>
        }
      />
    );
  },
);

DateTimePicker.displayName = 'DateTimePicker';

export { DateTimePicker, TimePickerInput, TimePicker, TimePickerType, Period };
export type { DateTimePickerProps, DateTimePickerRef };
